import { format } from 'date-fns';
import _ from 'lodash';
import Markdown from 'markdown-to-jsx';
import React, { useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Icon from '@mdi/react';
import { mdiClockOutline, mdiUpdate } from '@mdi/js';
import { MessagingContext } from '../../../services';
import { BaseInput } from '../../base';
import { AttachmentButton } from '../attachment-button';
import { NoteIndicator } from '../note-indicator';
import { MessageFields, IMessageFieldsValues } from '../message-fields';
import { ClaimsManager } from '../claims-manager';
import { DoneButton, UndoneButton, ReadButton, UnreadButton } from '..';
import { isHtmlContent, sanitizeHtml, normalizeMarkdown } from '../util';
import * as S from './chat-body.styles';
import {BlockedComponent} from "./blocked-component";
import { CoreFetch, MessageLinkEntityType } from '../../../api';
import { MessageApi } from '../../../api/server';
import { CodingsTable } from './codings-table';

interface IChatBody {
    hideNote?: boolean;
    hideDone?: boolean;
}

export const ChatBody: React.FunctionComponent<IChatBody> = (props: IChatBody): React.ReactElement => {
    const { hideNote, hideDone } = props;
    
    // Get context and data BEFORE any hooks
    const context = useContext(MessagingContext);
    const comment = _.first(context.comments);
    const isReadOnlyClaimChoice = context.isReadOnlyClaimChoice;
    const isSupportApp = context.account?.supportGrant === true;
    
    // Parse ISO date string to just date portion (yyyy-MM-dd)
    const parseDateOnlyInitial = (isoString: string | undefined | null): string | undefined => {
        if (!isoString) return undefined;
        return isoString.split('T')[0];
    };

    // All useState hooks MUST be called before any conditional returns
    const [isEditable, setEditable] = useState(false);
    const [isSubjectLoading, setSubjectLoading] = useState(false);
    const [subjectView, setSubjectView] = useState(context.message?.subject || '');
    const [locked, setLocked] = useState(false);
    const [messageFields, setMessageFields] = useState<IMessageFieldsValues>({
        priority: context.message.priority || undefined,
        dueDate: parseDateOnlyInitial(context.message.dueDate),
        categoryIds: _.map(context.message.links?.messageCategories, 'id') || [],
        assignedUserIds: _.map(context.message.links?.users, 'id') || [],
    });
    
    // Field-specific loading states
    const [isCategorySaving, setIsCategorySaving] = useState(false);
    const [isAssignedUsersSaving, setIsAssignedUsersSaving] = useState(false);
    
    // Ref to store debounced save function
    const debouncedSaveRef = useRef<ReturnType<typeof _.debounce> | null>(null);

    // NOW we can do conditional returns AFTER all hooks are declared
    if (!context.message || !comment) {
        return <></>;
    }

    const fmtDate = (d: Date): string => format(d, 'MMM d, yyyy h:mm a');
    const parseDate = (d: string | undefined): Date => (d ? new Date(d) : new Date());
    
    // Parse ISO date string to just date portion (yyyy-MM-dd)
    const parseDateOnly = (isoString: string | undefined | null): string | undefined => {
        if (!isoString) return undefined;
        // Extract just the date part from ISO string (e.g., '2025-10-21T00:00:00Z' -> '2025-10-21')
        return isoString.split('T')[0];
    };

    const hasNote = !hideNote && _.get(comment, 'note', false);

    const { created, updated, id, links } = context.message;
 
    const claimIds = useMemo(() => {
        return _.map(links?.claims, 'id') || [];
    }, [links?.claims]);
    
    const codingIds = useMemo(() => {
        return _.map(links?.codings, 'id') || [];
    }, [links?.codings]);
    
    const documents = _.filter(context.documents, document => {
        const commentDocs = _.get(comment, 'documents', []);
        return _.includes(commentDocs, document.id);
    });
    
    const renderAttachments = (): React.ReactElement[] | undefined => {
        if (!documents || !documents.length) {
            return;
        }
        return _.times(
            _.size(documents),
            (i: number): React.ReactElement => {
                const document = documents[i];
                const onDownload = async (): Promise<void> => {
                    if (document && document.id) {
                        await (context.onDocumentDownload && context.onDocumentDownload(document.id));
                    }
                };

                const onAssign = async (): Promise<void> => {
                    await (context.onDocumentAssign && context.onDocumentAssign(document));
                };

                const onEdit = async (): Promise<void> => {
                    await (context.onDocumentEdit && context.onDocumentEdit(document));
                };

                const canAssign = claimIds.length > 0 && !document.claimId && !!context.onDocumentAssign;

                return (
                    <AttachmentButton
                        key={`attachment-${i}`}
                        filename={document.filename as string}
                        filesize={document.filesize}
                        thumbnail={document.thumbnail}
                        onDownload={onDownload}
                        onAssign={canAssign ? onAssign : undefined}
                        onEdit={context.onDocumentEdit ? onEdit : undefined}
                        isSupportApp={isSupportApp}
                    />
                );
            },
        );
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSubjectView(e.target.value);
    };

    const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
        setSubjectView(e.target.value);
        if (!id) {
            throw new Error("Can't edit subject for this message");
        }
        setSubjectLoading(true);
        await context.onSubjectUpdate(id, e.target.value);
        setSubjectLoading(false);
        handleToggleEditable();
    };

    const handleToggleEditable = () => {
        if (!context.isSubjectEditable) {
            return;
        }

        setEditable(!isEditable);
    };

    const handleDoneClick = async (): Promise<void> => {
        if (!locked && context.message && context.message.id) {
            setLocked(true);
            await context.onMarkAsClosed(context.message.id);
            setLocked(false);
            context.onMessageClose();
        }
    };

    const handleUndoneClick = async (): Promise<void> => {
        if (!locked && context.message && context.message.id) {
            setLocked(true);
            await context.onMarkAsOpen(context.message.id);
            setLocked(false);
            context.onMessageClose();
        }
    };

    const handleReadClick = async (): Promise<void> => {
        if (!locked && context.message && context.message.id) {
            setLocked(true);
            await context.onMarkAsRead(context.message.id);
            setLocked(false);
            context.onMessageClose();
        }
    };

    const handleUnreadClick = async (): Promise<void> => {
        if (!locked && context.message && context.message.id) {
            setLocked(true);
            await context.onMarkAsUnread(context.message.id);
            setLocked(false);
            context.onMessageClose();
        }
    };

    const handleRemoveClaim = (claimId: string) => async () => {
        if (!id) {
            throw new Error("Can't edit claim for this message");
        }

        try {
            await context.onUnlinkEntityFromMessage?.(id, MessageLinkEntityType.CLAIM, [claimId]);
        } catch (error) {
            console.error('Failed to unlink claim:', error);
        }
    };

    const handleChangeClaim = async (claimId: string) => {
        if (!id) {
            throw new Error("Can't edit claim for this message");
        }
        await context.onLinkEntityToMessage?.(id, MessageLinkEntityType.CLAIM, [claimId]);
    };

    const handleBulkClaimsAdd = async (claimIds: string[]) => {
        if (!id) {
            throw new Error("Can't edit claim for this message");
        }
        await context.onLinkEntityToMessage?.(id, MessageLinkEntityType.CLAIM, claimIds);
    };

    const renderClaimButtons = (): React.ReactElement[] => {
        return _.map(claimIds, (claimId: string) => (
            <S.ClaimButton
                key={claimId}
                claimId={claimId}
                onRemove={handleRemoveClaim(claimId)}
                forceClaim={!!isReadOnlyClaimChoice}
            />
        ));
    };

    const handleMessageFieldsChange = (newValues: IMessageFieldsValues) => {
        setMessageFields(newValues);
    };

    // Check if there are actual changes
    const hasChanges = useCallback((): boolean => {
        const original = {
            priority: context.message.priority || undefined,
            dueDate: parseDateOnly(context.message.dueDate),
            categoryIds: _.map(context.message.links?.messageCategories, 'id') || [],
            assignedUserIds: _.map(context.message.links?.users, 'id') || []
        };
        
        return !_.isEqual(messageFields, original);
    }, [context.message.priority, context.message.dueDate, context.message.links?.messageCategories, context.message.links?.users, messageFields]);

    // Reusable save function
    const saveMessageFields = useCallback(async () => {
        if (!id) {
            return;
        }

        const original = {
            priority: context.message.priority || undefined,
            dueDate: parseDateOnly(context.message.dueDate),
            categoryIds: _.map(context.message.links?.messageCategories, 'id') || [],
            assignedUserIds: _.map(context.message.links?.users, 'id') || []
        };
        
        // Don't save if there are no changes
        if (_.isEqual(messageFields, original)) {
            return;
        }
        let hasError = false;

        setIsAssignedUsersSaving(true);
        try {
            const existingUserIds = _.map(context.message.links?.users, 'id') || [];
            const existingUserIdsSet = new Set(existingUserIds);
            const selectedUserIds = messageFields.assignedUserIds || [];
            
            const newUserIds = selectedUserIds.filter(userId => !existingUserIdsSet.has(userId));
            if (newUserIds.length > 0) {
                await context.onLinkEntityToMessage?.(id, MessageLinkEntityType.BANKUSER, newUserIds);
            }

            const selectedUserIdsSet = new Set(selectedUserIds);
            const removedUserIds = existingUserIds.filter(userId => !selectedUserIdsSet.has(userId));
            if (removedUserIds.length > 0) {
                await context.onUnlinkEntityFromMessage?.(id, MessageLinkEntityType.BANKUSER, removedUserIds);
            }
            setIsAssignedUsersSaving(false);
        } catch (error) {
            hasError = true;
            setIsAssignedUsersSaving(false);
        }

        setIsCategorySaving(true);
        try {
            const existingCategoryIds = _.map(context.message.links?.messageCategories, 'id') || [];
            const existingCategoryIdsSet = new Set(existingCategoryIds);
            const selectedCategoryIds = messageFields.categoryIds || [];
            
            const newCategoryIds = selectedCategoryIds.filter(catId => !existingCategoryIdsSet.has(catId));
            if (newCategoryIds.length > 0) {
                await context.onLinkEntityToMessage?.(id, MessageLinkEntityType.MESSAGECATEGORY, newCategoryIds);
            }

            const selectedCategoryIdsSet = new Set(selectedCategoryIds);
            const removedCategoryIds = existingCategoryIds.filter(catId => !selectedCategoryIdsSet.has(catId));
            if (removedCategoryIds.length > 0) {
                await context.onUnlinkEntityFromMessage?.(id, MessageLinkEntityType.MESSAGECATEGORY, removedCategoryIds);
            }
            setIsCategorySaving(false);
        } catch (error) {
            console.error('Failed to update categories:', error);
            hasError = true;
            setIsCategorySaving(false);
        }

        try {
            const previousPriority = context.message.priority;
            const newPriority = messageFields.priority;
            const didPriorityChange = newPriority !== previousPriority;
            
            const previousDueDateString = parseDateOnly(context.message.dueDate);
            const newDueDateString = messageFields.dueDate;
            const didDueDateChange = newDueDateString !== previousDueDateString;
            
            if (didPriorityChange || didDueDateChange) {
                
                let dueDateISO: string | null = null;
                if (newDueDateString) {
                    const [year, month, day] = newDueDateString.split('-').map(Number);
                    dueDateISO = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0)).toISOString();
                }
                
                if (isSupportApp) {
                    await CoreFetch(MessageApi).putSupportMessage({
                        id,
                        supportUpdateMessagePayload: {
                            priority: newPriority as any,
                            dueDate: dueDateISO || undefined
                        }
                    });
                } else {
                    await CoreFetch(MessageApi).putProviderMessage({
                        id,
                        providerUpdateMessagePayload: {
                            priority: newPriority as any,
                            dueDate: dueDateISO || undefined
                        }
                    });
                }
            }
        } catch (error) {
            console.error('Failed to update priority/due date:', error);
            hasError = true;
        }

        if (hasError) {
            throw new Error('Failed to update priority/due date');
        }
    }, [id, messageFields, context.message.priority, context.message.dueDate, context.message.links?.messageCategories, context.message.links?.users, context.onLinkEntityToMessage, context.onUnlinkEntityFromMessage]);

    useEffect(() => {
        if (context.message?.subject) {
            setSubjectView(context.message.subject);
        }
    }, [context.message?.subject]);

    // Update message fields when context.message changes
    useEffect(() => {
        setMessageFields({
            priority: context.message?.priority || undefined,
            dueDate: parseDateOnly(context.message?.dueDate),
            categoryIds: _.map(context.message?.links?.messageCategories, 'id') || [],
            assignedUserIds: _.map(context.message?.links?.users, 'id') || [],
        });
    }, [context.message?.id, context.message?.priority, context.message?.dueDate, context.message?.links?.messageCategories, context.message?.links?.users]);

    // Initialize debounced save function
    useEffect(() => {
        // Cancel previous debounced function if it exists
        if (debouncedSaveRef.current) {
            debouncedSaveRef.current.cancel();
        }
        
        debouncedSaveRef.current = _.debounce(saveMessageFields, 800);
        
        // Cleanup on unmount
        return () => {
            debouncedSaveRef.current?.cancel();
        };
    }, [saveMessageFields]);

    // Auto-save when messageFields change
    useEffect(() => {
        if (hasChanges() && debouncedSaveRef.current) {
            debouncedSaveRef.current();
        }
    }, [messageFields, hasChanges]);


    return (
        <S.Header note={hasNote}>
            {/* <S.Topic>{patientFirst} {datesOfService}</S.Topic> */}
            {isEditable ? (
                <S.EditableSubject title="Edit">
                    <BaseInput
                        onChange={handleChange}
                        disabled={isSubjectLoading}
                        onBlur={handleBlur}
                        value={subjectView}
                        autoFocus={true}
                    />
                </S.EditableSubject>
            ) : (
                <S.Subject onClick={handleToggleEditable}>{subjectView}</S.Subject>
            )}
            <S.Row>
                {!hideNote && <NoteIndicator comment={comment} />}
                <S.TimestampsWrapper>
                    <S.TimestampItem>
                        <Icon path={mdiClockOutline} size={0.7} color="#6B7280" />
                        <span>Created: {fmtDate(parseDate(created))}</span>
                    </S.TimestampItem>
                    <S.TimestampItem>
                        <Icon path={mdiUpdate} size={0.7} color="#6B7280" />
                        <span>Updated: {fmtDate(parseDate(updated))}</span>
                    </S.TimestampItem>
                </S.TimestampsWrapper>
            </S.Row>
            <S.Body>
                {isHtmlContent(comment.body) ? (
                    <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.body) }} />
                ) : (
                    <Markdown options={{
                        overrides: {
                            iframe: {
                                component: BlockedComponent
                            },
                        },
                    }}>{normalizeMarkdown(comment.body || '')}</Markdown>
                )}
            </S.Body>
            <S.Attachments>{renderAttachments()}</S.Attachments>
            <S.ActionRow>
                <S.ButtonsContainer>
                    {context.message.open && !hideDone && (
                        <DoneButton onClick={handleDoneClick} disabled={locked} />
                    )}
                    {!context.message.open && (
                        <UndoneButton onClick={handleUndoneClick} disabled={locked} />
                    )}
                    {!context.message.read && (
                        <ReadButton onClick={handleReadClick} disabled={locked} />
                    )}
                    {context.message.read && (
                        <UnreadButton onClick={handleUnreadClick} disabled={locked} />
                    )}
                </S.ButtonsContainer>
                <MessageFields 
                    values={messageFields}
                    onChange={handleMessageFieldsChange}
                    isCategorySaving={isCategorySaving}
                    isAssignedUsersSaving={isAssignedUsersSaving}
                />
            </S.ActionRow>
            <ClaimsManager
                messageId={id}
                claimIds={claimIds}
                onClaimAdd={handleChangeClaim}
                onBulkClaimsAdd={handleBulkClaimsAdd}
                onClaimRemove={(claimId: string) => handleRemoveClaim(claimId)()}
            />
            {codingIds.length > 0 && (
                <CodingsTable 
                    codingIds={codingIds}
                    isSupportApp={isSupportApp}
                />
            )}
        </S.Header>
    );
};
